/**
 * A header control that is an icon at every width.
 *
 * The merchant screens carry four of these between them — refresh on the board and on
 * the cook page, history, and the ordering control — and they were written once, on the
 * board, then needed again. An icon carries the action; the accessible name and the
 * tooltip carry the words. Keeping one copy is what stops the same button being a glyph
 * on one screen and a labelled button on the next.
 *
 * The trade is that a wordless control is slower to learn. It pays for itself in a
 * header that holds its shape: a row of labels beside a search field fits on a wide
 * screen and wraps a control at a time on a narrow one.
 */

import type { ComponentProps } from 'react';

import { Button } from './ui/button';
import { Icon } from './ui/icon';
import { Text } from './ui/text';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

/** Props for {@link IconControl}: what it does, how it looks, and its words. */
export interface IconControlProps {
  /** The accessible name, and the tooltip a pointer sees. */
  label: string;
  icon: ComponentProps<typeof Icon>['as'];
  onPress: () => void;
  disabled?: boolean;
}

/**
 * Renders the control.
 *
 * @param props Label, icon, handler and disabled state.
 * @returns The control.
 */
export function IconControl({ label, icon, onPress, disabled }: IconControlProps) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="secondary"
          className="w-9 px-0"
          onPress={onPress}
          disabled={disabled}
          accessibilityLabel={label}>
          <Icon as={icon} aria-hidden className="text-foreground size-4" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        <Text>{label}</Text>
      </TooltipContent>
    </Tooltip>
  );
}
